import { Suspense, useState } from "react";
import type { ShouldRevalidateFunction } from "react-router";
import { Await, useLoaderData, useParams, useSearchParams } from "react-router";

import {
  useContentfulInspectorMode,
  useContentfulLiveUpdates,
} from "@contentful/live-preview/react";
import { StarIcon } from "@heroicons/react/20/solid";
import { ArrowUturnLeftIcon, TruckIcon } from "@heroicons/react/24/outline";

import Breadcrumbs, { BreadcrumbsSkeleton } from "~/components/breadcrumbs";
// eslint-disable-next-line import/no-named-as-default
import VariantSelector from "~/components/products/product-variant-selector";
import { SkeletonAwait } from "~/components/suspense/skeleton";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "~/components/ui/accordion";
import { Button } from "~/components/ui/button";
import { Dialog, DialogContent } from "~/components/ui/dialog";
import ExpandableText from "~/components/ui/expandable-text";
import { Label } from "~/components/ui/label";
import { H3 } from "~/components/ui/text";
import { RichTextRender } from "~/contentful/components/rich-text";
import type { C_ProductInfo } from "~/lib/app.types";
import type { SerializeFrom } from "~/lib/types";
import { cn } from "~/lib/ui";
import { invariant } from "~/lib/utils/utils";
import { useFormatPrice } from "~/shop/utils/price";

import { headers as headersDefinition } from "./components/headers";
import { ProductBuyForm } from "./components/product-buy-form";
import { ProductImages } from "./components/product-images";
import { ProductQuickNav } from "./components/product-quick-nav";
import PDPTabs from "./components/product-tabs";
import PDPVideo from "./components/product-video";
import { ProductRecommendations } from "./components/products-recommendations";
import { loader as productDetailLoader } from "./loader/product-detail-loader";
import { useProductDetailsHistoryState } from "./location-state/product-details.location-state";

export { PDPErrorBoundary as ErrorBoundary } from "~/components/error-boundries/pdp-error-boundary";

export const loader = productDetailLoader;
export const headers = headersDefinition;

export const handle: AppHandle = {
  rootLayoutTransition: false,
};

export const shouldRevalidate: ShouldRevalidateFunction = ({
  formMethod,
  currentUrl,
  nextUrl,
}) => {
  if (formMethod !== "GET" && formMethod !== undefined) return true;
  if (currentUrl.pathname !== nextUrl.pathname) return true;
  return false;
};

function PriceLabel({
  salePrice,
  currentPrice,
  formatPrice,
}: {
  salePrice?: number | null;
  currentPrice?: number | null;
  formatPrice: ReturnType<typeof useFormatPrice>;
}) {
  if (!salePrice)
    return (
      <Label className="text-lg font-normal">{formatPrice(currentPrice)}</Label>
    );
  return (
    <Label className="text-lg font-normal">
      <span className="line-through">{formatPrice(currentPrice)}</span>
      <span className="pl-1 text-salePrice">{formatPrice(salePrice)}</span>
    </Label>
  );
}

function ReviewsSummary({
  averageRating,
  reviewCount,
}: {
  averageRating?: string | number | null;
  reviewCount?: string | number | null;
}) {
  const rating = Number(averageRating ?? 0);
  return (
    <div className="flex flex-row flex-wrap justify-start gap-2">
      <div className="flex flex-row items-center md:flex-col md:items-start lg:flex-row lg:items-center">
        <div className="flex items-center">
          {[0, 1, 2, 3, 4].map(r => (
            <StarIcon
              key={r}
              className={cn(
                Math.round(rating) > r
                  ? "text-neutralalpha-11"
                  : "text-transparent",
                "h-5 w-5 flex-shrink-0 stroke-neutralalpha-11",
              )}
              aria-hidden="true"
            />
          ))}
        </div>
        <p className="sr-only">{rating} out of 5 stars</p>
        <span className="ml-1 text-neutralalpha-11">{reviewCount} reviews</span>
      </div>
      <Button
        variant="link"
        onClick={() => alert("Coming soon")}
        className="px-0 text-base font-medium text-accentalpha-11"
      >
        Write a review
      </Button>
    </div>
  );
}

export default function Page() {
  const {
    master,
    data: _product,
    recomendationData,
    staticContent,
    videoContent,
    originalTabsContent,
    productCategories,
  } = useLoaderData<typeof loader>();
  const [searchParams] = useSearchParams();
  const preview = !!searchParams.get("preview");

  const liveContentText = useContentfulLiveUpdates(staticContent);
  const staticContentData = preview ? liveContentText : staticContent;

  const liveContentVideo = useContentfulLiveUpdates(videoContent);
  const videoContentData = preview ? liveContentVideo : videoContent;

  const inspectorProps = useContentfulInspectorMode();

  const [state] = useProductDetailsHistoryState();
  const [isSizeGuideDialogOpen, setIsSizeGuideDialogOpen] = useState<boolean>();
  const formatPrice = useFormatPrice();

  const product = state?.product ?? _product;
  const routeParams = useParams();
  invariant(routeParams.productId, "productId is required");

  const productId = routeParams.productId;

  return (
    <div className="">
      <div className="mx-auto py-16 sm:px-4 md:px-10 md:py-24">
        <div className="flex flex-row justify-between px-4 sm:px-0">
          <div className="w-full pb-5">
            <Suspense fallback={<BreadcrumbsSkeleton size={4} />}>
              <Await resolve={product}>
                {product => (
                  <SkeletonAwait
                    resolve={productCategories}
                    skeleton={<BreadcrumbsSkeleton size={4} />}
                  >
                    {productCategories => (
                      <Breadcrumbs
                        categories={productCategories}
                        pageName={product.name}
                      />
                    )}
                  </SkeletonAwait>
                )}
              </Await>
            </Suspense>
          </div>
          <div className="hidden sm:block">
            {state?.quickNav && (
              <ProductQuickNav
                product={product as SerializeFrom<C_ProductInfo>}
                state={state}
              />
            )}
          </div>
        </div>
        <div
          className={cn(
            "relative flex w-full flex-col gap-5 md:flex-row",
            state?.direction === "forward" && "animate-in slide-in-from-right",
            state?.direction === "back" && "animate-in slide-in-from-left",
          )}
          key={`reveal-${productId}`}
        >
          <div
            className={
              "flex w-full flex-col items-center overflow-hidden md:w-3/5"
            }
          >
            <Suspense fallback={<div>Loading...</div>}>
              <Await resolve={product}>
                {product => (
                  <ProductImages
                    product={product as SerializeFrom<C_ProductInfo>}
                  />
                )}
              </Await>
            </Suspense>
          </div>

          <div className="flex w-full flex-col items-center md:w-2/5">
            <Suspense fallback={<div>Loading...</div>}>
              <Await resolve={product}>
                {product => (
                  <div className="relative flex w-full flex-col gap-3 px-5 sm:px-36 md:pl-5 md:pr-0 lg:pl-14">
                    <ProductBuyForm
                      product={product}
                      productId={productId}
                      master={master}
                      openSizeGuide={() => setIsSizeGuideDialogOpen(true)}
                      wrapOptions
                    >
                      <div>
                        <div className="flex justify-between">
                          <h1 className="max-w- text-lg font-bold tracking-tight text-text-dark-on-light">
                            {product.name}
                          </h1>
                          <VariantSelector.SelectedVariant>
                            {({ salePrice, currentPrice }) => (
                              <PriceLabel
                                salePrice={salePrice}
                                currentPrice={currentPrice}
                                formatPrice={formatPrice}
                              />
                            )}
                          </VariantSelector.SelectedVariant>
                        </div>
                        {/* Reviews */}
                        <div>
                          <h3 className="sr-only">Reviews</h3>
                          <ReviewsSummary
                            averageRating={product.c_bvAverageRating}
                            reviewCount={product.c_bvReviewCount}
                          />
                        </div>
                      </div>
                    </ProductBuyForm>
                  </div>
                )}
              </Await>
            </Suspense>
            <section
              aria-label="features-section"
              className="mt-5 w-full pl-5 sm:px-36 md:pl-5 md:pr-0 lg:pl-14"
            >
              <div className="flex w-full flex-row justify-start gap-5">
                <div className="flex flex-row items-start justify-start gap-1">
                  <div className="flex">
                    <TruckIcon width={24} />
                  </div>
                  <div className="flex flex-col">
                    <Label className="text-sm font-normal text-text-dark-on-light">
                      FREE Delivery
                    </Label>
                    <Label className="text-xs font-light text-neutralalpha-11">
                      Over £30
                    </Label>
                  </div>
                </div>
                <div className="flex flex-row items-start justify-start gap-1">
                  <div className="flex">
                    <ArrowUturnLeftIcon width={24} />
                  </div>
                  <div className="flex flex-col">
                    <Label className="text-sm font-normal text-text-dark-on-light">
                      FREE Returns
                    </Label>
                    <Label className="text-xs font-light text-neutralalpha-11">
                      To a store
                    </Label>
                  </div>
                </div>
              </div>
            </section>
            <section
              aria-labelledby="details-heading"
              className="mt-5 w-full sm:px-36 md:pl-5 md:pr-0 lg:pl-14"
            >
              <Accordion type="single" collapsible defaultValue="details">
                <AccordionItem value="details">
                  <AccordionTrigger
                    className={
                      "self-center bg-white py-5 data-[state=open]:bg-inherit"
                    }
                  >
                    <span className="text-lg font-normal text-neutral-11">
                      Details
                    </span>
                  </AccordionTrigger>
                  <AccordionContent className="[&>div]:p-0">
                    <ExpandableText>
                      <Suspense fallback={<div>Loading...</div>}>
                        <Await resolve={product}>
                          {product => (
                            <div className="flex flex-col gap-5 text-base tracking-normal">
                              <div
                                dangerouslySetInnerHTML={{
                                  __html: product.longDescription ?? "",
                                }}
                              />
                              <h3 className="font-semibold">Care:</h3>
                              <div
                                dangerouslySetInnerHTML={{
                                  __html: product["c_care-instruction"] ?? "",
                                }}
                              />
                            </div>
                          )}
                        </Await>
                      </Suspense>
                    </ExpandableText>
                  </AccordionContent>
                </AccordionItem>
                <AccordionItem value="deliveryReturns">
                  <AccordionTrigger
                    className={
                      "self-center bg-white py-5 data-[state=open]:bg-inherit"
                    }
                  >
                    <span className="text-lg font-normal text-neutral-11">
                      Delivery & Returns
                    </span>
                  </AccordionTrigger>
                  <AccordionContent className="[&>div]:p-0">
                    <ExpandableText>
                      <Suspense fallback={<div>Loading...</div>}>
                        <Await resolve={staticContentData}>
                          {({
                            deliveryReturnsContent,
                            deliveryReturnsContentId,
                          }) =>
                            deliveryReturnsContent && (
                              <span
                                {...inspectorProps({
                                  entryId: deliveryReturnsContentId,
                                  fieldId: "name",
                                })}
                              >
                                <RichTextRender
                                  entryId={deliveryReturnsContentId ?? ""}
                                  content={deliveryReturnsContent}
                                />
                              </span>
                            )
                          }
                        </Await>
                      </Suspense>
                    </ExpandableText>
                  </AccordionContent>
                </AccordionItem>
                <AccordionItem value="sizeFit">
                  <AccordionTrigger
                    className={
                      "self-center bg-white py-5 data-[state=open]:bg-inherit"
                    }
                  >
                    <span className="text-lg font-normal text-neutral-11">
                      Size & Fit
                    </span>
                  </AccordionTrigger>
                  <AccordionContent className="[&>div]:p-0">
                    <ExpandableText>
                      <Suspense fallback={<div>Loading...</div>}>
                        <Await resolve={staticContentData}>
                          {({ sizeGuideContent, sizeGuideContentId }) =>
                            sizeGuideContent && (
                              <span
                                {...inspectorProps({
                                  entryId: sizeGuideContentId,
                                  fieldId: "name",
                                })}
                              >
                                <RichTextRender
                                  entryId={sizeGuideContentId ?? ""}
                                  content={sizeGuideContent}
                                />
                              </span>
                            )
                          }
                        </Await>
                      </Suspense>
                    </ExpandableText>
                  </AccordionContent>
                </AccordionItem>
                <AccordionItem value="loyalty">
                  <AccordionTrigger
                    className={
                      "self-center bg-white py-5 data-[state=open]:bg-inherit"
                    }
                  >
                    <span className="text-lg font-normal text-neutral-11">
                      Loyalty
                    </span>
                  </AccordionTrigger>
                  <AccordionContent className="[&>div]:p-0">
                    <ExpandableText>
                      <Suspense fallback={<div>Loading...</div>}>
                        <Await resolve={staticContentData}>
                          {({ loyaltyContent, loyaltyContentId }) =>
                            loyaltyContent && (
                              <span
                                {...inspectorProps({
                                  entryId: loyaltyContentId,
                                  fieldId: "name",
                                })}
                              >
                                <RichTextRender
                                  entryId={loyaltyContentId ?? ""}
                                  content={loyaltyContent}
                                />
                              </span>
                            )
                          }
                        </Await>
                      </Suspense>
                    </ExpandableText>
                  </AccordionContent>
                </AccordionItem>
              </Accordion>
            </section>
          </div>
        </div>
      </div>
      <Suspense fallback={<div>Loading...</div>}>
        <Await resolve={videoContentData}>
          {videoContentData =>
            videoContentData && <PDPVideo content={videoContentData} />
          }
        </Await>
      </Suspense>
      <Suspense fallback={<div>Loading...</div>}>
        <Await resolve={originalTabsContent}>
          {content => <PDPTabsRender content={content} />}
        </Await>
      </Suspense>

      <div aria-labelledby="carousel-heading" className="mt-10 w-full">
        <Label className="mb-4 inline-block w-full text-center text-lg">
          Recommendations
        </Label>
        <ProductRecommendations recomendationData={recomendationData} />
      </div>

      <Dialog
        open={isSizeGuideDialogOpen}
        onOpenChange={() => setIsSizeGuideDialogOpen(false)}
      >
        <DialogContent className="shadow-light-3 m-0 flex flex-col items-start self-stretch rounded-lg border border-neutralalpha-6 bg-page-background p-5 sm:w-[380px] md:w-[380px]">
          <div className="h-full w-full">
            <H3 className="mb-5">Size Guide</H3>
            <Suspense fallback={<div>Loading...</div>}>
              <Await resolve={staticContentData}>
                {({ sizeGuideContent, sizeGuideContentId }) =>
                  sizeGuideContent && (
                    <span
                      {...inspectorProps({
                        entryId: sizeGuideContentId,
                        fieldId: "name",
                      })}
                    >
                      <RichTextRender
                        entryId={sizeGuideContentId ?? ""}
                        content={sizeGuideContent}
                      />
                    </span>
                  )
                }
              </Await>
            </Suspense>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

const PDPTabsRender = ({
  content,
}: {
  content: Awaited<SerializeFrom<typeof loader>["originalTabsContent"]>;
}) => {
  const liveContentTabs = useContentfulLiveUpdates(content);
  const items = liveContentTabs?.data?.items;
  if (!items || !items.length) return null;
  return <PDPTabs content={items} />;
};
